import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Generic confirm / destructive-confirm dialog. Consolidates the hand-rolled
 * Dialog + Header + footer-buttons shell repeated across the settings dialogs
 * (delete workspace, remove org member, demote owner, …).
 *
 * The caller owns the mutation and passes `loading` (e.g. `mutation.isPending`)
 * — this component does not manage async state itself. Optional `children`
 * render between the description and the footer for any extra body content
 * (a picker, a warning line, an inline alert).
 */
export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label shown on the confirm button while `loading`. Defaults to "Working…". */
  loadingLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Drop the cancel button, leaving the confirm button alone — for a dialog
   * that INFORMS rather than asks ("Only your workspace owner can change the
   * plan", with a single "Close"). Two buttons that both just dismiss read as a
   * choice the user doesn't actually have.
   *
   * Only for dialogs whose confirm action is itself a dismissal. A dialog that
   * does something on confirm must keep its cancel — the ✕ and ESC are dismiss
   * affordances, not a visible way to decline.
   */
  hideCancel?: boolean;
  /** Render the confirm button with the destructive variant. */
  destructive?: boolean;
  /**
   * Cancel button variant. Defaults to `ghost` — pass `secondary` for the
   * bordered treatment the row actions use (`/api-keys`' Revoke).
   */
  cancelVariant?: 'ghost' | 'secondary';
  /**
   * Render both footer buttons at the 32px row-action height (`size="sm"` +
   * `height: 32`) instead of the default 40px dialog height — matching the
   * `/api-keys` card-header pair (Revoke / Create API key).
   */
  compactActions?: boolean;
  /** Disables both buttons and swaps the confirm label to `loadingLabel`. */
  loading?: boolean;
  /** Extra disable for the confirm button (beyond `loading`). */
  confirmDisabled?: boolean;
  onConfirm: () => void | Promise<void>;
  /** Extra className on the DialogContent (e.g. a max-width override). */
  contentClassName?: string;
  /** Optional body content rendered between the description and the footer. */
  children?: React.ReactNode;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  loadingLabel = 'Working…',
  cancelLabel = 'Cancel',
  hideCancel = false,
  destructive = false,
  cancelVariant = 'ghost',
  compactActions = false,
  loading = false,
  confirmDisabled = false,
  onConfirm,
  contentClassName,
  children,
}: ConfirmDialogProps) {
  const actionSize = compactActions ? 'sm' : undefined;
  const actionStyle = compactActions ? { height: 32 } : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-md', contentClassName)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter>
          {!hideCancel && (
            <Button
              variant={cancelVariant}
              size={actionSize}
              style={actionStyle}
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            variant={destructive ? 'destructive' : 'default'}
            size={actionSize}
            style={actionStyle}
            onClick={() => void onConfirm()}
            disabled={loading || confirmDisabled}
          >
            {loading ? loadingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
